import {
  Box,
  Button,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import PaginationControls from "components/fileStorage/PaginationControls";
import { MANAGEMENT_TABS_SX } from "components/management/managementTheme";
import ListToolbar from "components/secureExchange/ListToolbar";
import ProjectDialogs, {
  type ProjectAction,
} from "components/secureExchange/ProjectDialogs";
import ProjectTable, {
  type ProjectSort,
} from "components/secureExchange/ProjectTable";
import SecureExchangeBreadcrumbs from "components/secureExchange/SecureExchangeBreadcrumbs";
import SecureExchangeHeader from "components/secureExchange/SecureExchangeHeader";
import SecureExchangeShell from "components/secureExchange/SecureExchangeShell";
import SecureFileTable from "components/secureExchange/SecureFileTable";
import SecureUploadDialog from "components/secureExchange/SecureUploadDialog";
import SelectionBar, {
  SelectAllLink,
  SelectionAnnouncement,
} from "components/secureExchange/SelectionBar";
import {
  type FileMoves,
  type FileSort,
  fileMoves,
  nextFileSort,
} from "components/secureExchange/fileModel";
import { ClientIcon } from "components/secureExchange/icons";
import {
  type ProjectStatusFilter,
  projectStatusKey,
  projectsEmptyText,
  readProjectStatus,
} from "components/secureExchange/projectModel";
import { bulkActions } from "components/secureExchange/selectionModel";
import { useBulkFileActions } from "components/secureExchange/useBulkFileActions";
import { useDebouncedValue } from "components/secureExchange/useDebouncedValue";
import { useFileActions } from "components/secureExchange/useFileActions";
import { useFileSelection } from "components/secureExchange/useFileSelection";
import { useSecureExchangePaths } from "components/secureExchange/useSecureExchangePaths";
import {
  useGetSecureClientQuery,
  useGetSecureFilesQuery,
  useGetSecureProjectsQuery,
} from "features/secureUploads/secureUploads";
import { useAppSelector } from "features/store";
import { usePermissions } from "hooks/usePermissions";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";
import { localStorageUtils } from "utils/localStorage";

type ClientTab = "projects" | "files" | "deleted";

const PAGE_SIZE = 25;

function Pager({
  total,
  page,
  limit,
  onPage,
  onLimit,
}: {
  total: number;
  page: number;
  limit: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
}) {
  if (total === 0) return null;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <PaginationControls
      currentLimit={limit}
      onLimitChange={onLimit}
      totalItems={total}
      currentPage={page}
      totalPages={pages}
      onPageChange={onPage}
      size="small"
      hasNextPage={page < pages}
    />
  );
}

interface TabActions {
  /** New Project and Upload New File, as the tab shows them. */
  buttons: React.ReactNode;
}

function ProjectsTab({
  clientId,
  canOrganize,
  readOnly,
  buttons,
  onAction,
}: TabActions & {
  clientId: number;
  canOrganize: boolean;
  readOnly: boolean;
  onAction: (action: ProjectAction) => void;
}) {
  const paths = useSecureExchangePaths();
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  // Opens on the open projects; a person's own choice stays with them.
  const statusKey = projectStatusKey(
    useAppSelector((state) => state.auth.user?.id),
  );
  const [status, setStatus] = useState<ProjectStatusFilter>(() =>
    readProjectStatus(
      statusKey ? localStorageUtils.getItem<unknown>(statusKey, null) : null,
    ),
  );
  const [sort, setSort] = useState<ProjectSort | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data, isLoading, isFetching } = useGetSecureProjectsQuery({
    client_id: clientId,
    status: status || undefined,
    search: search || undefined,
    ordering: sort
      ? `${sort.direction === "desc" ? "-" : ""}${sort.column}`
      : undefined,
    page,
    page_size: limit,
  });

  return (
    <>
      <ListToolbar
        search={searchInput}
        onSearch={(value) => {
          setSearchInput(value);
          setPage(1);
        }}
        placeholder="Search Projects"
        filters={
          <Select
            size="small"
            value={status}
            displayEmpty
            onChange={(event) => {
              const next = event.target.value as ProjectStatusFilter;
              setStatus(next);
              if (statusKey) localStorageUtils.setItem(statusKey, next);
              setPage(1);
            }}
            inputProps={{ "aria-label": "Project status" }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        }
        actions={buttons}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          opacity: isFetching && !isLoading ? 0.6 : 1,
        }}
      >
        <ProjectTable
          projects={data?.results ?? []}
          loading={isLoading}
          sort={sort}
          onSort={(column) => {
            setSort((current) =>
              current?.column === column
                ? {
                    column,
                    direction: current.direction === "asc" ? "desc" : "asc",
                  }
                : { column, direction: column === "name" ? "asc" : "desc" },
            );
            setPage(1);
          }}
          empty={projectsEmptyText({ search, status, canOrganize })}
          projectPath={(project) => paths.project(clientId, project.id)}
          canOrganize={canOrganize}
          readOnly={readOnly}
          onAction={onAction}
        />
      </Box>
      <Pager
        total={data?.count ?? 0}
        page={page}
        limit={limit}
        onPage={setPage}
        onLimit={(next) => {
          setLimit(next);
          setPage(1);
        }}
      />
    </>
  );
}

function FilesTab({
  clientId,
  clientName,
  deleted,
  readOnly,
  moves,
  buttons,
}: TabActions & {
  clientId: number;
  clientName: string;
  deleted: boolean;
  readOnly: boolean;
  moves: FileMoves;
}) {
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [sort, setSort] = useState<FileSort | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const { data, isLoading, isFetching, error } = useGetSecureFilesQuery({
    tab: deleted ? "deleted" : "live",
    location: deleted ? undefined : "individual",
    client: clientId,
    search: search || undefined,
    sort_by: sort?.column ?? null,
    sort_order: sort?.direction ?? null,
    page,
    page_size: limit,
  });
  const rows = data?.results;
  const total = data?.count ?? 0;
  const picking = useFileSelection({
    rows,
    search,
    fileTotal: total,
    rowTotal: total,
    page,
    limit,
    setPage,
    error,
    everything: deleted
      ? { tab: "deleted", client: clientId, search: search || undefined }
      : {
          tab: "live",
          location: "individual",
          client: clientId,
          search: search || undefined,
        },
  });

  const focusSearch = () =>
    window.requestAnimationFrame(() => searchRef.current?.focus());
  const actions = useFileActions({ onChanged: picking.removed });
  const bulk = useBulkFileActions({
    clientId,
    clientName,
    place: ["Individual Files"],
    onDone: (ids) => {
      picking.removed(ids);
      picking.clear();
      focusSearch();
    },
    onGone: picking.clear,
  });

  return (
    <>
      <SelectionAnnouncement text={picking.announcement} />
      {picking.count > 0 ? (
        <SelectionBar
          label={picking.label}
          selectAll={
            picking.canTakeAll ? (
              <SelectAllLink
                total={total}
                busy={picking.selectingAll}
                onClick={() => void picking.takeAll()}
              />
            ) : null
          }
          actions={bulkActions(picking.picked, {
            place: deleted ? "deleted" : "individual",
            moves,
          })}
          readOnly={readOnly}
          busy={Boolean(bulk.progress)}
          progress={bulk.progress}
          onAction={(action) => bulk.run(action, picking.picked)}
          onClear={() => {
            picking.clear();
            focusSearch();
          }}
        />
      ) : (
        <ListToolbar
          search={searchInput}
          onSearch={(value) => {
            setSearchInput(value);
            setPage(1);
          }}
          placeholder={deleted ? "Search deleted files" : "Search files"}
          actions={buttons}
          inputRef={searchRef}
        />
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          opacity: isFetching && !isLoading ? 0.6 : 1,
        }}
      >
        <SecureFileTable
          files={rows ?? []}
          loading={isLoading}
          deleted={deleted}
          sort={sort}
          onSort={(column) => {
            setSort((current) => nextFileSort(current, column));
            setPage(1);
          }}
          empty={
            search
              ? "No files match."
              : deleted
                ? "No deleted files."
                : "No files here yet."
          }
          readOnly={readOnly}
          moves={moves}
          onAction={actions.run}
          selection={picking.table}
        />
      </Box>
      <Pager
        total={total}
        page={page}
        limit={limit}
        onPage={setPage}
        onLimit={(next) => {
          setLimit(next);
          setPage(1);
        }}
      />
      {actions.dialogs}
      {bulk.dialogs}
    </>
  );
}

/** A client's page: its projects, its Individual Files and, for Manage, the
 * files deleted there. */
export default function SecureExchangeClientPage() {
  const params = useParams<{ clientId: string }>();
  const clientId = Number(params.clientId);
  const paths = useSecureExchangePaths();
  const { readOnly } = useViewAs();
  const { sees } = usePermissions();
  const canOrganize = sees(ACTIONS.SECURE_EXCHANGE_ORGANIZE);
  const seesDeleted = sees(ACTIONS.SECURE_EXCHANGE_DELETED);
  const moves = fileMoves(sees);
  const [searchParams, setSearchParams] = useSearchParams();
  const [action, setAction] = useState<ProjectAction | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const {
    data: client,
    isLoading,
    isError,
  } = useGetSecureClientQuery(clientId, { skip: !clientId });
  const missing = !clientId || isError;

  const requested = searchParams.get("tab");
  const tab: ClientTab =
    requested === "files"
      ? "files"
      : requested === "deleted" && seesDeleted
        ? "deleted"
        : "projects";
  const openTab = (next: ClientTab) =>
    setSearchParams(next === "projects" ? {} : { tab: next }, {
      replace: true,
    });

  const newProject = canOrganize ? (
    <ReadOnlyTooltip active={readOnly}>
      <Button
        variant="outlined"
        disabled={readOnly}
        onClick={() => setAction({ kind: "create", clientId })}
      >
        New Project
      </Button>
    </ReadOnlyTooltip>
  ) : null;
  const upload = (
    <ReadOnlyTooltip active={readOnly}>
      <Button
        variant="contained"
        disabled={readOnly}
        onClick={() => setUploadOpen(true)}
      >
        Upload New File
      </Button>
    </ReadOnlyTooltip>
  );
  const buttons = (
    <>
      {newProject}
      {upload}
    </>
  );

  return (
    <SecureExchangeShell
      title={client ? `${client.name} - Secure Exchange` : "Secure Exchange"}
      breadcrumbs={
        <SecureExchangeBreadcrumbs
          items={[paths.home, { label: client?.name ?? "" }]}
        />
      }
      header={
        <SecureExchangeHeader
          icon={<ClientIcon />}
          title={missing ? "Client not found" : client?.name}
          loading={isLoading}
          counters={
            missing
              ? []
              : [
                  { label: "Open Projects", value: client?.open_projects },
                  { label: "Files", value: client?.files },
                  { label: "Contributors", value: client?.contributors },
                ]
          }
        />
      }
    >
      {missing ? (
        <Typography sx={{ p: 3 }} color="text.secondary">
          This client is not in your Secure Exchange.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              px: { xs: 1, sm: 2 },
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_event, next: ClientTab) => openTab(next)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={MANAGEMENT_TABS_SX}
            >
              <Tab value="projects" label="Projects" />
              <Tab value="files" label="Individual Files" />
              {seesDeleted ? (
                <Tab value="deleted" label="Deleted Files" />
              ) : null}
            </Tabs>
          </Box>
          {tab === "projects" ? (
            <ProjectsTab
              key={`projects-${clientId}`}
              clientId={clientId}
              canOrganize={canOrganize}
              readOnly={readOnly}
              buttons={buttons}
              onAction={setAction}
            />
          ) : (
            <FilesTab
              key={`${tab}-${clientId}`}
              clientId={clientId}
              clientName={client?.name ?? ""}
              deleted={tab === "deleted"}
              readOnly={readOnly}
              moves={moves}
              buttons={tab === "deleted" ? null : buttons}
            />
          )}
        </>
      )}
      <ProjectDialogs action={action} onClose={() => setAction(null)} />
      {client ? (
        <SecureUploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          clientId={clientId}
          clientName={client.name}
        />
      ) : null}
    </SecureExchangeShell>
  );
}
